import { Link } from "react-router-dom";

export const Privacy = () => {
  return (
    <div style={{
      maxWidth: "780px",
      margin: "0 auto",
      padding: "4rem 2rem",
      color: "#374151",
      lineHeight: "1.7"
    }}>
      <h1 style={{
        fontSize: "2.25rem",
        fontWeight: "800",
        color: "#111827",
        marginBottom: "0.5rem",
        lineHeight: "1.2"
      }}>
        Privacy Policy
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "2.5rem" }}>
        <strong>Effective date:</strong> April 26, 2026
      </p>

      <Section title="1. Who we are">
        <p>
          SHOWME Campaign Finances (&ldquo;<strong>SHOWME</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the website
          {" "}<a href="https://showmecampaignfinances.com">showmecampaignfinances.com</a>{" "}
          and the related software-as-a-service application that extracts
          structured data from Missouri Ethics Commission campaign finance
          reports. This Privacy Policy explains what information we collect,
          why we collect it, who we share it with, and the choices you have.
        </p>
        <p>
          If you have questions about this policy, contact us at{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <p>We collect only the information needed to operate the service.</p>
        <h3 style={subhead}>Information you give us</h3>
        <ul style={list}>
          <li>
            <strong>Account information.</strong> When you create an account,
            we collect your email address, a hashed password, and (optionally)
            your name.
          </li>
          <li>
            <strong>Payment information.</strong> If you subscribe to a paid
            tier, our payment processor Stripe collects and stores your payment
            details. We never see or store your card number; we receive only a
            customer identifier and the high-level subscription state.
          </li>
          <li>
            <strong>Uploaded documents.</strong> When you upload a campaign
            finance report PDF, we store the file and the structured data we
            extract from it. The underlying campaign finance reports are public
            records published by the Missouri Ethics Commission.
          </li>
          <li>
            <strong>Support correspondence.</strong> If you email us, we keep
            the message so we can reply and reference it later.
          </li>
        </ul>
        <h3 style={subhead}>Information collected automatically</h3>
        <ul style={list}>
          <li>
            <strong>Authentication and session data.</strong> When you log in,
            we issue a session token (JWT) that your browser sends back to
            identify you on subsequent requests.
          </li>
          <li>
            <strong>Server logs.</strong> Our servers record standard request
            metadata such as timestamps, IP addresses, requested URLs, and HTTP
            response codes for the purposes of security monitoring,
            troubleshooting, and abuse prevention.
          </li>
        </ul>
        <p>
          We do not use third-party advertising trackers, behavioral analytics,
          or social-media pixels.
        </p>
      </Section>

      <Section title="3. How we use information">
        <ul style={list}>
          <li>
            <strong>To provide the service.</strong> Authenticate you, store
            your uploaded reports, run extractions, and present analytics.
          </li>
          <li>
            <strong>To bill you.</strong> Process subscription payments through
            Stripe and send receipts.
          </li>
          <li>
            <strong>To communicate with you.</strong> Send transactional emails
            (account verification, password reset, billing receipts, support
            replies). We do not send marketing email.
          </li>
          <li>
            <strong>To secure the service.</strong> Detect and prevent abuse,
            unauthorized access, and other harm to users or our infrastructure.
          </li>
          <li>
            <strong>To comply with the law.</strong> Respond to lawful
            requests, enforce our Terms of Service, and protect our legal
            rights.
          </li>
        </ul>
      </Section>

      <Section title="4. Legal bases for processing (EEA / UK)">
        <p>
          If you are in the European Economic Area or the United Kingdom, we
          rely on the following legal bases under the GDPR and UK GDPR: (a)
          performance of a contract you entered into when you signed up; (b)
          our legitimate interests in operating, securing, and improving the
          service; and (c) compliance with legal obligations.
        </p>
      </Section>

      <Section title="5. Who we share information with">
        <p>
          We share information only with service providers that help us run
          SHOWME, and only to the extent they need it for that purpose:
        </p>
        <ul style={list}>
          <li>
            <strong>Stripe</strong> (payment processing). Subject to Stripe&rsquo;s
            privacy policy at{" "}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              stripe.com/privacy
            </a>.
          </li>
          <li>
            <strong>Amazon Web Services</strong> (cloud hosting and
            transactional email via Amazon SES, in the US East (N. Virginia)
            region).
          </li>
          <li>
            <strong>Domain registrar and DNS</strong> for routing requests to
            our service.
          </li>
        </ul>
        <p>
          We do not sell or rent your personal information. We do not share
          your information for behavioral advertising. We may disclose
          information if required by law, court order, or to enforce our
          rights or protect users.
        </p>
      </Section>

      <Section title="6. How long we keep information">
        <p>
          We keep account information for as long as your account is active.
          If you delete your account, we delete your personal information
          within 30 days, except where we are required to retain records for
          legal, tax, accounting, or fraud-prevention purposes (typically up to
          seven years for billing records).
        </p>
        <p>
          Uploaded campaign finance reports may be retained in deidentified
          form (without the uploader&rsquo;s identity) because the underlying
          documents are public records.
        </p>
      </Section>

      <Section title="7. Security">
        <p>
          We protect your information with industry-standard measures: data is
          encrypted in transit using TLS, stored in access-controlled cloud
          infrastructure, and subject to least-privilege access policies for
          our team. We use IAM-based authentication for our cloud services and
          do not store static cloud credentials in our application
          configuration.
        </p>
        <p>
          No system is perfectly secure, and we cannot guarantee that
          unauthorized access will never occur. If you become aware of a
          security issue, please contact us immediately at{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>.
        </p>
      </Section>

      <Section title="8. Your rights">
        <p>You have the following rights regarding your personal information:</p>
        <ul style={list}>
          <li>
            <strong>Access.</strong> Request a copy of the personal information
            we hold about you.
          </li>
          <li>
            <strong>Correction.</strong> Ask us to correct inaccurate
            information.
          </li>
          <li>
            <strong>Deletion.</strong> Ask us to delete your personal
            information, subject to legal retention requirements.
          </li>
          <li>
            <strong>Portability.</strong> Receive your information in a
            structured, machine-readable format.
          </li>
          <li>
            <strong>Objection / restriction.</strong> Object to or restrict
            certain processing, where applicable.
          </li>
          <li>
            <strong>Complaint.</strong> Lodge a complaint with your local data
            protection authority.
          </li>
        </ul>
        <p>
          California residents have additional rights under the CCPA / CPRA,
          including the right to know what categories of personal information
          we collect and the right to opt out of any &ldquo;sale&rdquo; or
          &ldquo;sharing&rdquo; of personal information. We do not sell or
          share personal information as those terms are defined under
          California law.
        </p>
        <p>
          To exercise any right, email us at{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>{" "}
          from the address associated with your account. We respond within 30
          days.
        </p>
      </Section>

      <Section title="9. Cookies and similar technologies">
        <p>
          We use only the cookies (and equivalent local storage) required to
          keep you logged in and to remember your preferences within the
          application. We do not use third-party tracking, advertising, or
          analytics cookies.
        </p>
      </Section>

      <Section title="10. Children">
        <p>
          The service is not directed to anyone under 18. We do not knowingly
          collect personal information from children under 13. If you believe
          a child has provided us with personal information, please contact us
          and we will delete it.
        </p>
      </Section>

      <Section title="11. International users">
        <p>
          We operate the service from the United States. If you access SHOWME
          from outside the United States, your information will be processed in
          the United States, where data-protection laws may differ from those
          in your country. By using the service, you consent to that transfer.
        </p>
      </Section>

      <Section title="12. Changes to this policy">
        <p>
          We may update this policy as the service evolves. When we make
          material changes, we will update the effective date above and, where
          appropriate, notify you by email or through the application before
          the change takes effect.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          Questions, requests, or concerns about this Privacy Policy? Email{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>.
        </p>
      </Section>

      <p style={{ marginTop: "3rem", color: "#6b7280", fontSize: "0.9rem" }}>
        See also our <Link to="/terms">Terms of Service</Link>.
      </p>
    </div>
  );
};

const subhead = {
  fontSize: "1.05rem",
  fontWeight: 600,
  color: "#111827",
  marginTop: "1.25rem",
  marginBottom: "0.5rem"
};

const list = {
  paddingLeft: "1.5rem",
  marginBottom: "1rem"
};

const Section = ({ title, children }) => (
  <section style={{ marginBottom: "2.25rem" }}>
    <h2 style={{
      fontSize: "1.35rem",
      fontWeight: 700,
      color: "#111827",
      marginBottom: "0.75rem"
    }}>
      {title}
    </h2>
    {children}
  </section>
);

export default Privacy;
